import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "@tanstack/react-router";

import type { AppProfile } from "@/lib/appProfile";
import { cn } from "@/lib/classNames";
import {
  getButtonClassName,
  MONOLITH_BOARD_STYLE,
  STUDIO_COLOR_HEX,
} from "@/components/dashboard/monolithBoardStyle";
import { useAuth } from "@/hooks/useAuth";

function getDisplayName(profile: Pick<AppProfile, "display_name" | "email">): string {
  const displayName = profile.display_name?.trim();
  if (displayName) {
    return displayName;
  }

  const email = profile.email?.trim();
  if (email) {
    return email.split("@")[0] || email;
  }

  return "Player";
}

function getInitial(displayName: string): string {
  const firstCharacter = Array.from(displayName.trim())[0];
  return firstCharacter ? firstCharacter.toUpperCase() : "?";
}

export default function AccountMenu({
  profile,
  className,
}: {
  profile: Pick<AppProfile, "display_name" | "email">;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const displayName = useMemo(() => getDisplayName(profile), [profile]);
  const initial = useMemo(() => getInitial(displayName), [displayName]);
  const { signOut } = useAuth();

  useEffect(() => {
    if (!open) {
      return;
    }

    function handlePointerDown(event: PointerEvent) {
      if (!menuRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setOpen(false);
      }
    }

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  return (
    <div ref={menuRef} className={cn("relative shrink-0", className)}>
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Open account menu for ${displayName}`}
        onClick={() => setOpen((current) => !current)}
        className={cn(
          "group flex h-12 w-12 items-center justify-center rounded-full border-[3px] border-neutral-950 bg-[#fff7ec] transition-transform hover:-translate-y-px focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#3e664d]",
          open && "-translate-y-px",
        )}
      >
        <span
          aria-hidden="true"
          className="flex h-8 w-8 items-center justify-center rounded-full border-2 border-neutral-950 text-sm font-black uppercase tracking-[0.08em] text-[#fff7ec]"
          style={{ backgroundColor: STUDIO_COLOR_HEX.verdantDeep }}
        >
          {initial}
        </span>
      </button>

      <div
        role="menu"
        aria-label="Account menu"
        hidden={!open}
        className={cn(
          "absolute right-0 top-[calc(100%+0.75rem)] z-20 w-[min(16rem,calc(100vw-2rem))] p-3",
          MONOLITH_BOARD_STYLE.panelClassName,
        )}
      >
        <div className="border-b-2 border-neutral-950/15 pb-3">
          <p className="text-[10px] font-semibold uppercase tracking-[0.32em] text-neutral-600">Signed in</p>
          <p className="mt-2 truncate text-sm font-bold uppercase tracking-[-0.02em] text-neutral-950">{displayName}</p>
        </div>

        <div className="mt-3 space-y-2">
          <Link
            to="/profile"
            role="menuitem"
            onClick={() => setOpen(false)}
            className={getButtonClassName({
              variant: "muted",
              chrome: "compact",
              className:
                "flex w-full justify-between border-[3px] border-neutral-950 px-3 py-2 text-left text-[10px] tracking-[0.2em]",
            })}
          >
            <span>Profile</span>
          </Link>

          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              void signOut();
            }}
            className={getButtonClassName({
              variant: "accentSecondary",
              chrome: "compact",
              className:
                "flex w-full justify-between border-[3px] border-neutral-950 px-3 py-2 text-left text-[10px] tracking-[0.2em]",
            })}
          >
            <span>Log out</span>
          </button>
        </div>
      </div>
    </div>
  );
}
